/**
 * Модуль для формирования групп перед боем
 * Поддерживает режимы: 1v1, 3v3, 5v5
 * Этап 1: UI формирования групп
 */

import { escapeHtml } from '../utils/dom.js';

/**
 * Создаёт менеджер формирования групп
 */
export function createFormationManager({
  userCollection,
  safeFetch,
  apiUrl,
  buildAuthHeaders,
  getTelegramWebAppSafe,
  runBattleSimulation,
  currentUser
}) {
  // Загружаем сохраненные формации из localStorage
  const STORAGE_KEY = 'battle_formations';
  function loadFormationsFromStorage() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          '1v1': Array.isArray(parsed['1v1']) ? parsed['1v1'] : [],
          '3v3': Array.isArray(parsed['3v3']) ? parsed['3v3'] : [],
          '5v5': Array.isArray(parsed['5v5']) ? parsed['5v5'] : []
        };
      }
    } catch (error) {
      console.warn('[FormationManager] Failed to load formations from storage:', error);
    }
    return {
      '1v1': [],
      '3v3': [],
      '5v5': []
    };
  }
  
  // Валидирует и восстанавливает формации из сохраненных данных
  function validateAndRestoreFormations(savedFormations, userCollection) {
    const validated = {
      '1v1': [],
      '3v3': [],
      '5v5': []
    };
    
    // Создаем карту существ по ID для быстрого поиска
    const collectionMap = new Map();
    if (Array.isArray(userCollection)) {
      userCollection.forEach(creature => {
        const id = creature.id || creature.userCreatureId;
        if (id) {
          collectionMap.set(String(id), creature);
        }
      });
    }
    
    // Валидируем каждую формацию
    ['1v1', '3v3', '5v5'].forEach(mode => {
      const saved = savedFormations[mode] || [];
      saved.forEach(savedCreature => {
        const id = savedCreature.id || savedCreature.userCreatureId;
        if (id && collectionMap.has(String(id))) {
          // Существо существует в коллекции - восстанавливаем его
          validated[mode].push(collectionMap.get(String(id)));
        }
      });
    });
    
    return validated;
  }
  
  function saveFormationsToStorage(formations) {
    try {
      // Сохраняем только ID существ для экономии места
      const toSave = {
        '1v1': (formations['1v1'] || []).map(c => ({
          id: c.id || c.userCreatureId,
          creatureId: c.creatureId || c.creature_id
        })),
        '3v3': (formations['3v3'] || []).map(c => ({
          id: c.id || c.userCreatureId,
          creatureId: c.creatureId || c.creature_id
        })),
        '5v5': (formations['5v5'] || []).map(c => ({
          id: c.id || c.userCreatureId,
          creatureId: c.creatureId || c.creature_id
        }))
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (error) {
      console.warn('[FormationManager] Failed to save formations to storage:', error);
    }
  }

  /**
   * Восстанавливает формации из сохраненных данных после загрузки коллекции
   */
  function restoreFormationsFromStorage(userCollection) {
    if (!userCollection || !Array.isArray(userCollection) || userCollection.length === 0) {
      console.log('[FormationManager] No collection to restore formations from');
      return;
    }
    
    const savedFormations = loadFormationsFromStorage();
    const validated = validateAndRestoreFormations(savedFormations, userCollection);
    
    // Обновляем формации только если они изменились
    let hasChanges = false;
    ['1v1', '3v3', '5v5'].forEach(mode => {
      const currentIds = (state.playerFormation[mode] || []).map(c => String(c.id || c.userCreatureId)).sort();
      const validatedIds = validated[mode].map(c => String(c.id || c.userCreatureId)).sort();
      
      if (JSON.stringify(currentIds) !== JSON.stringify(validatedIds)) {
        state.playerFormation[mode] = validated[mode];
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      saveFormationsToStorage(state.playerFormation);
      // Слоты обновятся автоматически при переключении на вкладку FORMATION
      // или при следующем вызове updateFormationSlots
      console.log('[FormationManager] Formations restored from storage');
    }
  }

  const state = {
    currentMode: '1v1', // '1v1' | '3v3' | '5v5'
    playerFormation: loadFormationsFromStorage(), // Загружаем из localStorage
    selectedCard: null, // ID выбранной карты для добавления в формацию
    initialized: false
  };

  /**
   * Инициализирует UI формирования групп
   */
  function initFormationUI() {
    if (state.initialized) return;
    
    const battleScreen = document.getElementById('battle-screen');
    if (!battleScreen) {
      console.warn('[FormationManager] Battle screen not found');
      return;
    }

    // Проверяем, не создан ли уже UI
    if (document.getElementById('battle-formation-container')) {
      console.log('[FormationManager] UI already initialized');
      return;
    }

    // Создаём новый UI для формирования групп
    const formationHTML = `
      <div class="battle-formation-container" id="battle-formation-container">
        <!-- Tabs -->
        <div class="battle-formation-tabs">
          <button class="battle-formation-tab active" data-tab="formation">FORMATION</button>
          <button class="battle-formation-tab" data-tab="collection">CARDS</button>
        </div>

        <!-- Formation Tab -->
        <div class="battle-formation-tab-content active" id="formation-tab-content">
          <!-- Mode Selector -->
          <div class="battle-mode-selector">
            <div class="battle-mode-btn active" data-mode="1v1">1 vs 1</div>
            <div class="battle-mode-btn" data-mode="3v3">3 vs 3</div>
            <div class="battle-mode-btn" data-mode="5v5">5 vs 5</div>
          </div>

          <!-- Player Formation -->
          <div class="battle-formation-area">
            <div class="battle-formation-title">YOUR TEAM</div>
            <div class="battle-card-slots" id="player-formation-slots"></div>
          </div>

          <!-- Start Battle Button -->
          <button class="btn" id="start-battle-btn" style="width: 100%; margin-top: 16px;">START BATTLE</button>
        </div>

        <!-- Collection Tab -->
        <div class="battle-formation-tab-content" id="collection-tab-content">
          <div class="battle-formation-title">YOUR COLLECTION</div>
          <div class="battle-collection-grid" id="battle-collection-grid"></div>
        </div>
      </div>
    `;

    // Находим старый UI и скрываем его
    const oldSetupCard = battleScreen.querySelector('.battle-setup-card');
    if (oldSetupCard) {
      oldSetupCard.style.display = 'none';
    }

    // Вставляем новый UI после заголовка
    const title = battleScreen.querySelector('h2');
    if (title && title.nextSibling) {
      title.insertAdjacentHTML('afterend', formationHTML);
    } else {
      battleScreen.insertAdjacentHTML('beforeend', formationHTML);
    }

    // Инициализируем обработчики
    setupTabs();
    setupModeSelector();
    setupEventListeners();
    // Обновляем слоты с текущими формациями (из localStorage)
    updateFormationSlots();
    // НЕ загружаем коллекцию сразу - только при переключении на вкладку CARDS
    // Это экономит ресурсы и загружает данные только когда нужно

    state.initialized = true;
    console.log('[FormationManager] UI initialized');
  }

  /**
   * Настройка табов
   */
  function setupTabs() {
    const tabs = document.querySelectorAll('.battle-formation-tab');
    if (!tabs || tabs.length === 0) {
      console.error('[FormationManager] Tabs not found!');
      return;
    }
    
    console.log('[FormationManager] Setting up', tabs.length, 'tabs');
    
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = tab.dataset.tab;
        console.log('[FormationManager] Tab clicked:', tabName);
        
        // Обновляем табы
        document.querySelectorAll('.battle-formation-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        
        // Обновляем контент
        document.querySelectorAll('.battle-formation-tab-content').forEach(content => {
          content.classList.remove('active');
        });
        const contentTab = document.getElementById(`${tabName}-tab-content`);
        if (contentTab) {
          contentTab.classList.add('active');
          console.log('[FormationManager] Tab content activated:', contentTab.id);
        } else {
          console.error('[FormationManager] Tab content not found:', `${tabName}-tab-content`);
        }
        
        // Загружаем коллекцию при переключении на вкладку CARDS
        if (tabName === 'collection') {
          console.log('[FormationManager] Switching to CARDS tab, loading collection...');
          // Небольшая задержка для гарантии, что DOM обновлен
          setTimeout(() => {
            loadCollection();
          }, 50);
        } else {
          console.log('[FormationManager] Switching to FORMATION tab');
        }
      });
    });
  }

  /**
   * Настройка селектора режима
   */
  function setupModeSelector() {
    document.querySelectorAll('.battle-mode-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.battle-mode-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const newMode = btn.dataset.mode;
        state.currentMode = newMode;
        
        // Инициализируем формацию для режима, если её нет
        if (!state.playerFormation[newMode]) {
          state.playerFormation[newMode] = [];
        }
        
        // Сохраняем в localStorage при смене режима
        saveFormationsToStorage(state.playerFormation);
        
        // Обновляем слоты (каждый режим имеет свою формацию)
        updateFormationSlots();
      });
    });
  }

  /**
   * Обновляет слоты формации
   */
  function updateFormationSlots() {
    const slotsContainer = document.getElementById('player-formation-slots');
    if (!slotsContainer) return;

    const slotCount = getSlotCount();
    slotsContainer.className = `battle-card-slots battle-slots-${state.currentMode}`;
    slotsContainer.innerHTML = '';

    for (let i = 0; i < slotCount; i++) {
      const slot = document.createElement('div');
      slot.className = 'battle-card-slot';
      slot.dataset.slot = i;
      
      const currentFormation = state.playerFormation[state.currentMode] || [];
      if (currentFormation[i]) {
        const card = createCardElement(currentFormation[i], 'player');
        // Добавляем кнопку удаления
        const removeBtn = document.createElement('button');
        removeBtn.className = 'battle-card-remove';
        removeBtn.innerHTML = '×';
        removeBtn.title = 'Удалить из формации';
        removeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          removeCardFromFormation(i);
        });
        card.appendChild(removeBtn);
        // Клик на карту открывает коллекцию для замены
        card.addEventListener('click', () => {
          switchToTab('collection');
        });
        slot.appendChild(card);
      } else {
        slot.innerHTML = '<div class="battle-slot-label">Нажмите, чтобы добавить карту</div>';
        slot.style.cursor = 'pointer';
        slot.addEventListener('click', (e) => {
          e.stopPropagation();
          console.log('[FormationManager] Empty slot clicked, switching to CARDS tab');
          switchToTab('collection');
        });
      }
      
      slotsContainer.appendChild(slot);
    }
    
    // Если есть пустые слоты, показываем подсказку
    const emptySlots = slotCount - state.playerFormation.length;
    if (emptySlots > 0 && state.playerFormation.length > 0) {
      // Можно добавить визуальную подсказку о том, что можно добавить еще существ
    }
  }

  /**
   * Загружает коллекцию существ
   */
  async function loadCollection() {
    const collectionGrid = document.getElementById('battle-collection-grid');
    if (!collectionGrid) {
      console.error('[FormationManager] Collection grid not found');
      return;
    }

    console.log('[FormationManager] Loading collection...');

    // Показываем индикатор загрузки
    collectionGrid.innerHTML = '<div class="battle-empty-collection">Загрузка коллекции...</div>';

    // Пробуем использовать переданную коллекцию
    let collection = userCollection;
    console.log('[FormationManager] Initial userCollection:', collection?.length || 0, 'items');
    
    // Если коллекция пуста, загружаем с сервера
    if (!collection || !Array.isArray(collection) || collection.length === 0) {
      console.log('[FormationManager] Collection is empty, fetching from server...');
      try {
        const response = await safeFetch(`${apiUrl}/user/me`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...buildAuthHeaders()
          }
        });

        console.log('[FormationManager] Server response:', response);

        if (response && response.success && response.data && response.data.success) {
          // Endpoint /user/me возвращает { success: true, user, collection }
          collection = response.data.collection || [];
          console.log('[FormationManager] Loaded collection from server:', collection.length, 'items');
          
          // Восстанавливаем формации из сохраненных данных
          restoreFormationsFromStorage(collection);
        } else {
          console.warn('[FormationManager] Failed to load collection - invalid response:', response);
          collection = [];
        }
      } catch (error) {
        console.error('[FormationManager] Error loading collection:', error);
        collectionGrid.innerHTML = `<div class="battle-empty-collection">Ошибка загрузки: ${error.message || 'Неизвестная ошибка'}</div>`;
        return;
      }
    } else {
      console.log('[FormationManager] Using existing userCollection:', collection.length, 'items');
    }

    if (!collection || !Array.isArray(collection) || collection.length === 0) {
      console.log('[FormationManager] Collection is empty after loading');
      collectionGrid.innerHTML = '<div class="battle-empty-collection">Коллекция пуста</div>';
      return;
    }

    console.log('[FormationManager] Rendering', collection.length, 'creatures');
    collectionGrid.innerHTML = '';

    let renderedCount = 0;
    collection.forEach((creature, index) => {
      try {
        // Логируем для отладки
        console.log(`[FormationManager] Creating card ${index + 1}/${collection.length} for creature:`, {
          name: creature.name,
          id: creature.id,
          creatureId: creature.creatureId,
          level: creature.level,
          hasBaseAttributes: !!creature.baseAttributes
        });
        const cardElement = createCardElement(creature, 'collection');
        if (cardElement) {
          cardElement.addEventListener('click', () => addCardToFormation(creature));
          collectionGrid.appendChild(cardElement);
          renderedCount++;
        } else {
          console.error(`[FormationManager] createCardElement returned null for creature ${index}:`, creature);
        }
      } catch (error) {
        console.error(`[FormationManager] Error creating card for creature ${index}:`, error, creature);
      }
    });
    
    console.log('[FormationManager] Collection rendered, total cards:', collectionGrid.children.length, 'rendered:', renderedCount);
    
    if (renderedCount === 0 && collection.length > 0) {
      console.error('[FormationManager] No cards were rendered despite having collection items!');
      collectionGrid.innerHTML = '<div class="battle-empty-collection">Ошибка отображения коллекции. Проверьте консоль.</div>';
    }
  }

  /**
   * Создаёт элемент карты
   */
  function createCardElement(creature, context) {
    const cardDiv = document.createElement('div');
    cardDiv.className = `battle-card battle-card-${context}`;
    // Получаем ID существа (может быть userCreatureId или creatureId)
    const userCreatureId = creature.id || creature.userCreatureId;
    const creatureId = creature.creatureId || creature.creature_id;
    
    if (userCreatureId) {
      cardDiv.dataset.creatureId = userCreatureId;
    }
    if (creatureId) {
      cardDiv.dataset.baseCreatureId = creatureId;
    }

    const name = creature.name || 'Unknown';
    const level = creature.level || 1;
    const professionLevel = creature.profession_level || creature.professionLevel || 0;
    
    // Получаем редкость
    const rarityRaw = creature.rarity || (creature.baseAttributes?.rarity) || (creature.base_attributes?.rarity);
    const rarity = (() => {
      if (typeof rarityRaw === 'string') return rarityRaw.toLowerCase();
      if (rarityRaw && typeof rarityRaw === 'object' && rarityRaw.grade) return String(rarityRaw.grade).toLowerCase();
      return 'common';
    })();
    
    // Получаем изображение существа
    const imageUrl = creature.imageUrl || creature.image_url || 
                     `/creatures/${creatureId || 1}.png`;
    
    // Получаем базовые характеристики (как в Cs функции)
    const baseAttributes = creature.baseAttributes || creature.base_attributes || {};
    const baseStatsRaw = baseAttributes.baseStats || baseAttributes.base_stats || baseAttributes.stats || {};
    
    // Базовые значения по редкости
    const RARITY_BASE_STATS = {
      common: { hp: 100, attack: 20, defense: 15, speed: 10, critChance: 0.05, energy: 100 },
      rare: { hp: 112, attack: 23, defense: 18, speed: 11.5, critChance: 0.06, energy: 105 },
      epic: { hp: 126, attack: 27, defense: 22, speed: 13, critChance: 0.07, energy: 112 },
      legendary: { hp: 140, attack: 32, defense: 26, speed: 15.5, critChance: 0.09, energy: 120 }
    };
    const DEFAULT_STATS = { hp: 100, attack: 20, defense: 15, speed: 10, critChance: 0.05, energy: 100 };
    const baseline = (rarity && RARITY_BASE_STATS[rarity]) ? RARITY_BASE_STATS[rarity] : DEFAULT_STATS;
    
    // Используем базовую статистику из данных или значения по умолчанию
    const stats = {
      hp: baseStatsRaw.hp !== undefined ? baseStatsRaw.hp : baseline.hp,
      attack: baseStatsRaw.attack !== undefined ? baseStatsRaw.attack : baseline.attack,
      defense: baseStatsRaw.defense !== undefined ? baseStatsRaw.defense : baseline.defense,
      speed: baseStatsRaw.speed !== undefined ? baseStatsRaw.speed : baseline.speed,
      critChance: baseStatsRaw.critChance !== undefined ? baseStatsRaw.critChance : baseline.critChance,
      energy: baseStatsRaw.energy !== undefined ? baseStatsRaw.energy : baseline.energy
    };
    
    // Применяем бонусы от уровня (как в computeCombatStats и Cs функции)
    const safeLevel = Math.max(1, Math.min(level, 20));
    const safeProfessionLevel = Math.max(0, Math.min(professionLevel, 10));
    
    // Бонусы от уровня
    const hpBonus = safeLevel * 6;
    const attackBonus = safeLevel * 1.8;
    const defenseBonus = safeLevel * 1.5;
    const speedBonus = safeLevel * 1.2;
    const critBonus = safeProfessionLevel * 0.002;
    
    // Применяем бонусы с ограничениями
    const hp = Math.min(stats.hp + hpBonus, 260);
    const attack = Math.min(stats.attack + attackBonus, 50);
    const defense = Math.min(stats.defense + defenseBonus, 50);
    const speed = Math.min(stats.speed + speedBonus, 35);
    const critChance = Math.min(stats.critChance + critBonus, 0.5);
    const energy = Math.min(stats.energy + safeProfessionLevel * 1.5, 140);
    
    // Получаем способности
    const abilities = Array.isArray(baseAttributes.abilities) ? baseAttributes.abilities : [];
    const ultimate = baseAttributes.ultimate || null;

    cardDiv.innerHTML = `
      <div class="battle-card-image">
        <img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(name)}" 
             onerror="this.onerror=null; this.src='/creatures/1.png';" />
      </div>
      <div class="battle-card-header">
        <div class="battle-card-name">${escapeHtml(name)}</div>
        <div class="battle-card-level">Lv. ${level}</div>
        ${rarity !== 'common' ? `<div class="battle-card-rarity rarity-${rarity}">${rarity.toUpperCase()}</div>` : ''}
      </div>
      <div class="battle-card-stats">
        <div class="battle-stat">
          <div class="battle-stat-value">${attack.toFixed(1)}</div>
          <div class="battle-stat-label">ATK</div>
        </div>
        <div class="battle-stat">
          <div class="battle-stat-value">${defense.toFixed(1)}</div>
          <div class="battle-stat-label">DEF</div>
        </div>
        <div class="battle-stat">
          <div class="battle-stat-value">${hp}</div>
          <div class="battle-stat-label">HP</div>
        </div>
        <div class="battle-stat">
          <div class="battle-stat-value">${speed.toFixed(1)}</div>
          <div class="battle-stat-label">SPD</div>
        </div>
      </div>
      ${abilities.length > 0 || ultimate ? `
        <div class="battle-card-abilities">
          ${abilities.length > 0 ? `
            <div class="battle-card-ability">
              <div class="battle-card-ability-name">${escapeHtml(abilities[0].name || 'Способность')}</div>
              <div class="battle-card-ability-effect">${escapeHtml(abilities[0].effect || '')}</div>
            </div>
          ` : ''}
          ${ultimate ? `
            <div class="battle-card-ultimate">
              <div class="battle-card-ultimate-label">🌀 Ультимативная</div>
              <div class="battle-card-ability-name">${escapeHtml(ultimate.name || 'Ультимативная способность')}</div>
              <div class="battle-card-ability-effect">${escapeHtml(ultimate.effect || '')}</div>
            </div>
          ` : ''}
        </div>
      ` : ''}
      ${context !== 'collection' ? `
        <div class="battle-card-hp-bar">
          <div class="battle-card-hp-fill" style="width: 100%"></div>
        </div>
      ` : ''}
    `;

    return cardDiv;
  }

  /**
   * Добавляет карту в формацию
   */
  function addCardToFormation(creature) {
    const slotCount = getSlotCount();
    
    const currentFormation = state.playerFormation[state.currentMode] || [];
    
    // Проверяем, не добавлена ли уже эта карта в текущую формацию
    const creatureId = creature.id || creature.userCreatureId;
    const existingIndex = currentFormation.findIndex(c => (c.id || c.userCreatureId) === creatureId);
    
    if (existingIndex !== -1) {
      // Существо уже в формации - ничего не делаем (или можно показать сообщение)
      const app = getTelegramWebAppSafe();
      app?.showAlert?.('Эта карта уже в формации!');
      switchToTab('formation');
      return;
    }
    
    // Если формация заполнена, заменяем последнее существо
    if (currentFormation.length >= slotCount) {
      // Заменяем последнее существо
      currentFormation[slotCount - 1] = {...creature};
    } else {
      // Добавляем в первый свободный слот
      currentFormation.push({...creature});
    }
    
    // Обновляем формацию для текущего режима
    state.playerFormation[state.currentMode] = currentFormation;
    
    // Сохраняем в localStorage
    saveFormationsToStorage(state.playerFormation);
    
    // Если это режим 1v1, обновляем существо на главном экране
    if (state.currentMode === '1v1') {
      console.log('[FormationManager] addCardToFormation 1v1, formation:', currentFormation);
      if (currentFormation[0] && typeof window.setHomeScreenCreature === 'function') {
        try {
          console.log('[FormationManager] Calling setHomeScreenCreature with:', currentFormation[0]);
          window.setHomeScreenCreature(currentFormation[0]);
        } catch (error) {
          console.warn('[FormationManager] Failed to update home screen creature from formation:', error);
        }
      } else {
        console.warn('[FormationManager] setHomeScreenCreature not available or empty formation');
      }
    }

    updateFormationSlots();
    switchToTab('formation');
  }

  /**
   * Удаляет карту из формации
   */
  function removeCardFromFormation(index) {
    const currentFormation = state.playerFormation[state.currentMode] || [];
    currentFormation.splice(index, 1);
    state.playerFormation[state.currentMode] = currentFormation;
    
    // Сохраняем в localStorage
    saveFormationsToStorage(state.playerFormation);
    
    // Если это режим 1v1, после удаления можно обновить существо на главном экране
    if (state.currentMode === '1v1') {
      console.log('[FormationManager] removeCardFromFormation 1v1, formation:', currentFormation);
      if (typeof window.setHomeScreenCreature === 'function') {
        try {
          const nextCreature = currentFormation[0] || null;
          console.log('[FormationManager] Next creature for home screen:', nextCreature);
          if (nextCreature) {
            window.setHomeScreenCreature(nextCreature);
          }
        } catch (error) {
          console.warn('[FormationManager] Failed to update home screen creature after removal:', error);
        }
      }
    }

    updateFormationSlots();
  }

  /**
   * Получает количество слотов для текущего режима
   */
  function getSlotCount() {
    return state.currentMode === '1v1' ? 1 : state.currentMode === '3v3' ? 3 : 5;
  }

  /**
   * Переключает таб
   */
  function switchToTab(tabName) {
    console.log('[FormationManager] switchToTab called with:', tabName);
    
    document.querySelectorAll('.battle-formation-tab').forEach(tab => {
      if (tab.dataset.tab === tabName) {
        tab.classList.add('active');
        console.log('[FormationManager] Tab activated:', tabName);
      } else {
        tab.classList.remove('active');
      }
    });
    
    document.querySelectorAll('.battle-formation-tab-content').forEach(content => {
      content.classList.remove('active');
    });
    const contentTab = document.getElementById(`${tabName}-tab-content`);
    if (contentTab) {
      contentTab.classList.add('active');
      console.log('[FormationManager] Tab content activated:', contentTab.id);
      
      // Если переключились на коллекцию, загружаем её
      if (tabName === 'collection') {
        console.log('[FormationManager] Switching to collection tab, loading collection...');
        setTimeout(() => {
          loadCollection();
        }, 100);
      }
    } else {
      console.error('[FormationManager] Tab content not found:', `${tabName}-tab-content`);
    }
  }

  /**
   * Запускает бой
   */
  async function startBattle() {
    const slotCount = getSlotCount();
    const currentFormation = state.playerFormation[state.currentMode] || [];
    
    if (currentFormation.length === 0) {
      const app = getTelegramWebAppSafe();
      app?.showAlert?.('Добавьте хотя бы одну карту в формацию!');
      return;
    }

    // Разрешаем запуск с неполной формацией (опционально)
    // Можно раскомментировать для строгой валидации:
    // if (currentFormation.length < slotCount) {
    //   const app = getTelegramWebAppSafe();
    //   app?.showAlert?.(`Заполните все слоты формации (${slotCount})!`);
    //   return;
    // }

    // Сохраняем формацию в глобальное состояние для использования в simulator.js
    window.__battleFormation = {
      player: [...currentFormation],
      mode: state.currentMode
    };

    // Вызываем существующую функцию запуска боя
    // Она будет использовать window.__battleFormation для получения формации
    if (runBattleSimulation) {
      await runBattleSimulation();
    } else {
      console.error('[FormationManager] runBattleSimulation not provided');
      const app = getTelegramWebAppSafe();
      app?.showAlert?.('Ошибка: функция запуска боя не найдена');
    }
  }

  /**
   * Настройка обработчиков событий
   */
  function setupEventListeners() {
    const startBtn = document.getElementById('start-battle-btn');
    if (startBtn) {
      startBtn.addEventListener('click', startBattle);
    }
  }

  return {
    init: initFormationUI,
    getState: () => ({...state}),
    getFormation: () => ({
      player: [...(state.playerFormation[state.currentMode] || [])],
      mode: state.currentMode
    }),
    reset: () => {
      state.playerFormation = {
        '1v1': [],
        '3v3': [],
        '5v5': []
      };
      state.currentMode = '1v1';
      saveFormationsToStorage(state.playerFormation);
      updateFormationSlots();
    },
    resetMode: (mode) => {
      if (state.playerFormation[mode]) {
        state.playerFormation[mode] = [];
        if (state.currentMode === mode) {
          updateFormationSlots();
        }
      }
    }
  };
}

